import { useState, useMemo } from 'react';
import { 
  Users, Mail, Trash2, Search, ChevronDown, ChevronUp, 
  MapPin, Calendar, Phone, Shield, ShieldCheck, Star,
  Send, UserCheck, UserX, Download, Eye, X, Award,
  TrendingUp, UserPlus, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_CONFIG = {
  bronce: { color: '#CD7F32', bg: 'rgba(205,127,50,0.15)', label: 'Bronce', min: 0 },
  plata: { color: '#C0C0C0', bg: 'rgba(192,192,192,0.15)', label: 'Plata', min: 500 },
  oro: { color: '#FFD700', bg: 'rgba(255,215,0,0.15)', label: 'Oro', min: 1500 },
};

export function UserManagement({ members, memberStats, authHeader, onRefresh }) {
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedMember, setSelectedMember] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Filtered and sorted members
  const filteredMembers = useMemo(() => {
    let result = [...members];

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(m =>
        (m.first_name || '').toLowerCase().includes(q) ||
        (m.last_name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.member_number || '').toLowerCase().includes(q) ||
        ((m.address?.city) || '').toLowerCase().includes(q) ||
        (m.phone || '').includes(q)
      );
    }

    // Level filter
    if (filterLevel !== 'all') {
      result = result.filter(m => (m.benefits?.level || 'bronce') === filterLevel);
    }

    // Status filter
    if (filterStatus === 'active') result = result.filter(m => m.active !== false);
    if (filterStatus === 'inactive') result = result.filter(m => m.active === false);
    if (filterStatus === 'verified') result = result.filter(m => m.email_verified === true);
    if (filterStatus === 'unverified') result = result.filter(m => m.email_verified !== true);

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc': return (b.member_since || '').localeCompare(a.member_since || '');
        case 'date_asc': return (a.member_since || '').localeCompare(b.member_since || '');
        case 'name_asc': return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        case 'points_desc': return (b.benefits?.points || 0) - (a.benefits?.points || 0);
        default: return 0;
      }
    });

    return result;
  }, [members, search, filterLevel, filterStatus, sortBy]);

  const handleDelete = async (member) => {
    if (!window.confirm(`¿Eliminar a ${member.first_name} ${member.last_name} (${member.email})? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${member.id}`, { headers: { Authorization: authHeader } });
      toast.success('Miembro eliminado');
      onRefresh();
      if (detailOpen && selectedMember?.id === member.id) {
        setDetailOpen(false);
        setSelectedMember(null);
      }
    } catch {
      toast.error('Error al eliminar miembro');
    }
  };

  const handleToggleStatus = async (member) => {
    const newActive = member.active === false;
    try {
      await axios.put(`${API}/admin/members/${member.id}/status?active=${newActive}`, {}, { headers: { Authorization: authHeader } });
      toast.success(newActive ? 'Socio activado' : 'Socio desactivado');
      onRefresh();
    } catch {
      toast.error('Error al cambiar estado');
    }
  };

  const handleExportCSV = () => {
    if (filteredMembers.length === 0) return;
    const headers = ['Numero', 'Nombre', 'Apellido', 'Email', 'Telefono', 'Ciudad', 'Nivel', 'Puntos', 'Verificado', 'Activo', 'Comunicaciones', 'Miembro desde'];
    const rows = filteredMembers.map(m => [
      m.member_number,
      m.first_name,
      m.last_name,
      m.email,
      m.phone || '',
      m.address?.city || '',
      (m.benefits?.level || 'bronce').toUpperCase(),
      m.benefits?.points || 0,
      m.email_verified ? 'Si' : 'No',
      m.active !== false ? 'Si' : 'No',
      m.accept_communications ? 'Si' : 'No',
      m.member_since ? new Date(m.member_since).toLocaleDateString('es-ES') : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `socios_myeventcinema_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filteredMembers.length} socios exportados`);
  };

  const openDetail = (member) => {
    setSelectedMember(member);
    setDetailOpen(true);
  };

  const LevelBadge = ({ level }) => {
    const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.bronce;
    return (
      <span 
        data-testid={`level-badge-${level}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium" 
        style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}40` }}
      >
        <Award size={10} /> {cfg.label}
      </span>
    );
  };

  return (
    <div data-testid="user-management-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard data-testid="stat-total" icon={<Users size={18} />} value={memberStats.total} label="Total Socios" color="var(--primary)" />
        <StatCard data-testid="stat-active" icon={<UserCheck size={18} />} value={memberStats.active} label="Activos" color="#22c55e" />
        <StatCard data-testid="stat-verified" icon={<CheckCircle size={18} />} value={memberStats.verified || 0} label="Verificados" color="#3b82f6" />
        <StatCard data-testid="stat-new-month" icon={<UserPlus size={18} />} value={memberStats.new_this_month || 0} label="Nuevos este mes" color="#a855f7" />
        <StatCard data-testid="stat-comms" icon={<Mail size={18} />} value={memberStats.accept_communications || 0} label="Acepta Emails" color="#f59e0b" />
        <StatCard data-testid="stat-levels" icon={<Award size={18} />} 
          value={
            <div className="flex gap-2 text-sm">
              <span style={{ color: LEVEL_CONFIG.oro.color }}>{memberStats.by_level?.oro || 0}</span>
              <span style={{ color: LEVEL_CONFIG.plata.color }}>{memberStats.by_level?.plata || 0}</span>
              <span style={{ color: LEVEL_CONFIG.bronce.color }}>{memberStats.by_level?.bronce || 0}</span>
            </div>
          } 
          label="Oro / Plata / Bronce" color="var(--primary)" 
        />
      </div>

      {/* Distribution row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-[var(--surface)] border border-white/5">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-3">Top Ciudades</p>
          <div className="space-y-2">
            {Object.entries(memberStats.by_city || {})
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([city, count]) => (
                <div key={city} className="flex items-center justify-between">
                  <span className="text-white/70 text-sm flex items-center gap-2"><MapPin size={12} className="text-[var(--primary)]" />{city}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[var(--primary)]" style={{ width: `${(count / memberStats.total) * 100}%` }} /></div>
                    <span className="text-[var(--primary)] text-xs w-6 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div className="p-4 bg-[var(--surface)] border border-white/5">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-3">Top Generos Favoritos</p>
          <div className="space-y-2">
            {Object.entries(memberStats.by_genre || {})
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([genre, count]) => (
                <div key={genre} className="flex items-center justify-between">
                  <span className="text-white/70 text-sm capitalize flex items-center gap-2"><Star size={12} className="text-[var(--primary)]" />{genre}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[var(--primary)]" style={{ width: `${(count / memberStats.total) * 100}%` }} /></div>
                    <span className="text-[var(--primary)] text-xs w-6 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Toolbar: Search, Filters, Export */}
      <div className="bg-[var(--surface)] border border-white/5 mb-0">
        <div className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input
              data-testid="member-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, ciudad, telefono..."
              className="bg-transparent border-white/20 text-white rounded-none pl-10"
            />
          </div>
          
          <select
            data-testid="filter-level"
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="bg-transparent border border-white/20 text-white text-sm px-3 py-2 outline-none"
          >
            <option value="all" className="bg-[var(--surface)]">Todos los niveles</option>
            <option value="bronce" className="bg-[var(--surface)]">Bronce</option>
            <option value="plata" className="bg-[var(--surface)]">Plata</option>
            <option value="oro" className="bg-[var(--surface)]">Oro</option>
          </select>

          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-transparent border border-white/20 text-white text-sm px-3 py-2 outline-none"
          >
            <option value="all" className="bg-[var(--surface)]">Todos los estados</option>
            <option value="active" className="bg-[var(--surface)]">Activos</option>
            <option value="inactive" className="bg-[var(--surface)]">Inactivos</option>
            <option value="verified" className="bg-[var(--surface)]">Verificados</option>
            <option value="unverified" className="bg-[var(--surface)]">Sin verificar</option>
          </select>

          <select
            data-testid="sort-members"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent border border-white/20 text-white text-sm px-3 py-2 outline-none"
          >
            <option value="date_desc" className="bg-[var(--surface)]">Mas recientes</option>
            <option value="date_asc" className="bg-[var(--surface)]">Mas antiguos</option>
            <option value="name_asc" className="bg-[var(--surface)]">Nombre A-Z</option>
            <option value="points_desc" className="bg-[var(--surface)]">Mas puntos</option>
          </select>

          <Button 
            data-testid="export-csv-btn"
            variant="ghost" 
            onClick={handleExportCSV} 
            className="text-white/50 hover:text-white text-xs"
            disabled={filteredMembers.length === 0}
          >
            <Download size={14} className="mr-1" /> CSV
          </Button>
        </div>
        
        <div className="px-4 pb-3 text-white/40 text-xs">
          Mostrando {filteredMembers.length} de {members.length} socios
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-[var(--surface)] border border-white/5 border-t-0 overflow-x-auto">
        {filteredMembers.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="text-white/10 mx-auto mb-4" />
            <p className="text-white/40">{members.length === 0 ? 'No hay socios registrados' : 'No se encontraron resultados'}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                <th className="text-left p-3 pl-4">Socio</th>
                <th className="text-left p-3 hidden md:table-cell">Email</th>
                <th className="text-left p-3 hidden lg:table-cell">Ciudad</th>
                <th className="text-center p-3">Nivel</th>
                <th className="text-center p-3 hidden md:table-cell">Puntos</th>
                <th className="text-center p-3 hidden lg:table-cell">Estado</th>
                <th className="text-center p-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredMembers.map(member => {
                const level = member.benefits?.level || 'bronce';
                const points = member.benefits?.points || 0;
                const isActive = member.active !== false;
                const isVerified = member.email_verified === true;

                return (
                  <tr key={member.id} data-testid={`member-row-${member.id}`} className="hover:bg-white/5 transition-colors group">
                    <td className="p-3 pl-4">
                      <div className="flex items-center gap-3 min-w-[180px]">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-green-500' : 'bg-red-500/50'}`} />
                        <div>
                          <button 
                            onClick={() => openDetail(member)} 
                            className="text-white font-medium hover:text-[var(--primary)] transition-colors text-left"
                            data-testid={`member-name-${member.id}`}
                          >
                            {member.first_name} {member.last_name}
                          </button>
                          <p className="text-white/30 text-xs">{member.member_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="text-white/60">{member.email}</span>
                    </td>
                    <td className="p-3 hidden lg:table-cell">
                      <span className="text-white/50 text-xs">{member.address?.city || '-'}</span>
                    </td>
                    <td className="p-3 text-center">
                      <LevelBadge level={level} />
                    </td>
                    <td className="p-3 text-center hidden md:table-cell">
                      <span className="text-white/70 font-mono text-xs">{points} pts</span>
                    </td>
                    <td className="p-3 text-center hidden lg:table-cell">
                      <div className="flex items-center justify-center gap-2">
                        {isVerified ? (
                          <span className="text-green-500 text-xs flex items-center gap-1"><ShieldCheck size={12} /> Verificado</span>
                        ) : (
                          <span className="text-amber-500/60 text-xs flex items-center gap-1"><AlertCircle size={12} /> Pendiente</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          data-testid={`view-member-${member.id}`}
                          onClick={() => openDetail(member)}
                          className="p-1.5 text-white/30 hover:text-white transition-colors"
                          title="Ver detalle"
                        >
                          <Eye size={15} />
                        </button>
                        <a
                          data-testid={`email-member-${member.id}`}
                          href={`mailto:${member.email}`}
                          className="p-1.5 text-white/30 hover:text-[var(--primary)] transition-colors"
                          title="Enviar email"
                        >
                          <Send size={15} />
                        </a>
                        <button
                          data-testid={`toggle-status-${member.id}`}
                          onClick={() => handleToggleStatus(member)}
                          className={`p-1.5 transition-colors ${isActive ? 'text-white/30 hover:text-amber-500' : 'text-amber-500/50 hover:text-green-500'}`}
                          title={isActive ? 'Desactivar' : 'Activar'}
                        >
                          {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
                        </button>
                        <button
                          data-testid={`delete-member-${member.id}`}
                          onClick={() => handleDelete(member)}
                          className="p-1.5 text-white/30 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Mass email */}
      {members.length > 0 && (
        <div className="mt-4 flex items-center gap-4 px-1">
          <a
            data-testid="email-all-members-btn"
            href={`mailto:?bcc=${members.filter(m => m.accept_communications !== false).map(m => m.email).join(',')}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)]/20 border border-[var(--primary)]/40 text-[var(--primary)] text-xs hover:bg-[var(--primary)]/30 transition-colors"
          >
            <Mail size={14} />
            Email a todos ({members.filter(m => m.accept_communications !== false).length})
          </a>
          <span className="text-white/30 text-xs">Solo socios con comunicaciones aceptadas</span>
        </div>
      )}

      {/* Member Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[var(--bg)] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="member-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-lg">
              <Users size={20} className="text-[var(--primary)]" />
              Detalle del Socio
            </DialogTitle>
          </DialogHeader>
          {selectedMember && <MemberDetailContent member={selectedMember} onDelete={handleDelete} onToggleStatus={handleToggleStatus} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, value, label, color, ...rest }) {
  return (
    <div className="p-4 bg-[var(--surface)] border border-white/5 text-center" {...rest}>
      <div className="flex items-center justify-center gap-2 mb-1" style={{ color }}>
        {icon}
        <span className="text-2xl font-['Playfair_Display']">{typeof value === 'number' ? value : value}</span>
      </div>
      <p className="text-white/40 text-xs uppercase tracking-widest">{label}</p>
    </div>
  );
}

function MemberDetailContent({ member, onDelete, onToggleStatus }) {
  const level = member.benefits?.level || 'bronce';
  const levelCfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.bronce;
  const points = member.benefits?.points || 0;
  const isActive = member.active !== false;
  const isVerified = member.email_verified === true;

  const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 py-2">
      <span className="text-[var(--primary)] mt-0.5">{icon}</span>
      <div>
        <p className="text-white/40 text-xs uppercase tracking-wider">{label}</p>
        <p className="text-white/90 text-sm">{value || '-'}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header with name and level */}
      <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10">
        <div>
          <h3 className="text-xl font-['Playfair_Display'] text-white">{member.first_name} {member.last_name}</h3>
          <p className="text-white/40 text-xs mt-1">{member.member_number}</p>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium" style={{ background: levelCfg.bg, color: levelCfg.color, border: `1px solid ${levelCfg.color}40` }}>
            <Award size={14} /> {levelCfg.label}
          </span>
          <p className="text-white/50 text-xs mt-1 font-mono">{points} puntos</p>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${isActive ? 'bg-green-500/15 text-green-500 border border-green-500/30' : 'bg-red-500/15 text-red-500 border border-red-500/30'}`}>
          {isActive ? <UserCheck size={12} /> : <UserX size={12} />}
          {isActive ? 'Activo' : 'Inactivo'}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${isVerified ? 'bg-blue-500/15 text-blue-500 border border-blue-500/30' : 'bg-amber-500/15 text-amber-500 border border-amber-500/30'}`}>
          {isVerified ? <ShieldCheck size={12} /> : <AlertCircle size={12} />}
          {isVerified ? 'Email verificado' : 'Email sin verificar'}
        </span>
        {member.accept_communications && (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[var(--primary)]/15 text-[var(--primary)] border border-[var(--primary)]/30">
            <Mail size={12} /> Acepta comunicaciones
          </span>
        )}
      </div>

      {/* Contact Info */}
      <div className="divide-y divide-white/5">
        <InfoRow icon={<Mail size={14} />} label="Email" value={member.email} />
        <InfoRow icon={<Phone size={14} />} label="Telefono" value={member.phone} />
        <InfoRow icon={<MapPin size={14} />} label="Direccion" value={
          member.address ? [member.address.street, member.address.city, member.address.postal_code, member.address.province].filter(Boolean).join(', ') : '-'
        } />
        <InfoRow icon={<Calendar size={14} />} label="Fecha de nacimiento" value={member.birth_date || '-'} />
        <InfoRow icon={<Calendar size={14} />} label="Miembro desde" value={member.member_since ? new Date(member.member_since).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'} />
      </div>

      {/* Favorite genres */}
      {member.favorite_genres?.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Generos favoritos</p>
          <div className="flex flex-wrap gap-1.5">
            {member.favorite_genres.map(g => (
              <span key={g} className="px-2 py-0.5 bg-[var(--primary)]/15 text-[var(--primary)] text-xs capitalize border border-[var(--primary)]/20">{g}</span>
            ))}
          </div>
        </div>
      )}

      {/* Favorite cinemas */}
      {member.favorite_cinemas?.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Cines favoritos</p>
          <div className="flex flex-wrap gap-1.5">
            {member.favorite_cinemas.map(c => (
              <span key={c} className="px-2 py-0.5 bg-white/5 text-white/60 text-xs border border-white/10">{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Points History */}
      {member.points_history?.length > 0 && (
        <div>
          <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Historial de puntos</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {member.points_history.slice().reverse().map((ph, i) => (
              <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white/5">
                <span className="text-white/70">{ph.description}</span>
                <div className="flex items-center gap-3">
                  <span className={`font-mono ${ph.points > 0 ? 'text-green-500' : 'text-red-400'}`}>
                    {ph.points > 0 ? '+' : ''}{ph.points}
                  </span>
                  <span className="text-white/30">{ph.timestamp ? new Date(ph.timestamp).toLocaleDateString('es-ES') : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referral */}
      {member.referral_code && (
        <div className="p-3 bg-white/5 border border-white/10">
          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Codigo de referido</p>
          <p className="text-white font-mono text-sm">{member.referral_code}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        <a
          href={`mailto:${member.email}`}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--primary)]/20 border border-[var(--primary)]/40 text-[var(--primary)] text-sm hover:bg-[var(--primary)]/30 transition-colors"
          data-testid="detail-email-btn"
        >
          <Send size={14} /> Enviar email
        </a>
        <button
          onClick={() => onToggleStatus(member)}
          className={`flex items-center justify-center gap-2 px-4 py-2 text-sm transition-colors border ${
            isActive 
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-500 hover:bg-amber-500/20' 
              : 'bg-green-500/10 border-green-500/30 text-green-500 hover:bg-green-500/20'
          }`}
          data-testid="detail-toggle-status-btn"
        >
          {isActive ? <><UserX size={14} /> Desactivar</> : <><UserCheck size={14} /> Activar</>}
        </button>
        <button
          onClick={() => onDelete(member)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-500 text-sm hover:bg-red-500/20 transition-colors"
          data-testid="detail-delete-btn"
        >
          <Trash2 size={14} /> Eliminar
        </button>
      </div>
    </div>
  );
}
